import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Link, MenuItem, Select, Stack } from "@mui/material";
import { useNavigate } from "react-router";

import { IsUnauthorizedError, useLogin } from "providers/Login";

import { relativeTimeMs } from "../Detail/components/format";
import { SearchIndexStream, missingFrom, recordsPhrase } from "./searchIndexTypes";
import { listSearchIndexes, setSearchReadIndex } from "./searchIndexesApi";

// SearchIndexNotice says which index this listing came from, and lets an admin
// switch to another one.
//
// The log can be spread over more than one index — an upgrade hands writes to
// the new version and the older records stay behind until somebody copies them
// — and none of that is visible from the rows themselves. So the notice always
// names the index being read, and the picker is here rather than only in
// Services because this is where somebody realises a record is missing.
//
// It stays silent when the status cannot be fetched: a non-admin session
// cannot read it, and an unexplained banner would be worse than none.
export default function SearchIndexNotice(props: { stream: string, onIndexChange?: () => void }) {
    const login = useLogin();
    const navigate = useNavigate();
    const [status, setStatus] = useState<SearchIndexStream | null>(null);
    const [switching, setSwitching] = useState(false);

    const req = login.GetAxios();
    const reqRef = useRef(req);
    reqRef.current = req;

    // isActive is passed in by the caller that can still be unmounted while
    // this is in flight. It has to be checked around setStatus, not after the
    // call returns: load is what sets the state, so a guard on the outside
    // guards nothing.
    const load = useCallback(async (isActive: () => boolean = () => true) => {
        const r = reqRef.current;
        if (!r) return;
        try {
            const streams = await listSearchIndexes(r);
            if (!isActive()) return;
            setStatus(streams.find((s) => s.stream === props.stream) || null);
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            // Most likely a non-admin session, which cannot read the registry.
            // Say nothing rather than raise an alarm.
        }
    }, [props.stream]);

    useEffect(() => {
        let active = true;
        load(() => active);
        return () => { active = false; };
    }, [load]);

    // A stream with no registry record yet reports an empty readIndex and no
    // indices. Rendering that produced "Reading  — every record is here": an
    // empty name plus a false all-clear. It is not only the boot window — a
    // cluster that refuses the index create leaves the registry empty for
    // good — so say nothing here and let the Services panel, which can
    // explain it and offer the retry, be where that surfaces.
    if (!status || !status.readIndex) return null;

    // Where the missing records are, if any: the fullest index holding records
    // this one does not.
    const elsewhere = missingFrom(status, status.readIndex)[0];
    const readEntry = status.indices.find((i) => i.name === status.readIndex);
    const retired = readEntry?.state === "frozen";
    const inUse = status.indices.find((i) => i.state === "active");
    const degraded = (status.readSubstrFields?.length || 0) < (status.fullSubstrFields?.length || 0);
    const running = status.migration?.state === "running";

    // Every registered index is readable: a retired one still holds its
    // records, and reaching them is what this control is for.
    const readable = status.indices;

    const switchTo = async (index: string) => {
        const r = reqRef.current;
        if (!r || index === status.readIndex) return;
        setSwitching(true);
        try {
            setStatus(await setSearchReadIndex(r, status.stream, index));
            // The rows on screen came from the old index, so the page has to
            // fetch them again.
            props.onIndexChange?.();
        } catch (err) {
            if (IsUnauthorizedError(err)) {
                return; //GetAxios already dropped the session and redirected
            }
            await load();
        } finally {
            setSwitching(false);
        }
    };

    return <Alert
        severity={retired || elsewhere || degraded ? "warning" : "info"}
        sx={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}
        action={readable.length > 1 && (
            <Stack direction="row" alignItems="center" gap={1}>
                <Select
                    size="small"
                    value={status.readIndex}
                    disabled={switching}
                    onChange={(e) => switchTo(String(e.target.value))}
                    inputProps={{ "aria-label": "Index to read" }}
                >
                    {readable.map((index) => (
                        <MenuItem key={index.name} value={index.name}>{index.name}</MenuItem>
                    ))}
                </Select>
            </Stack>
        )}
    >
        {retired
            ? <>Reading <b>{status.readIndex}</b> — stopped {relativeTimeMs(readEntry?.disabledAt)}.
                {inUse ? <> New records are in <b>{inUse.name}</b>.</> : null}</>
            : elsewhere
                ? (running
                    ? <>Reading <b>{status.readIndex}</b>. Copying older records from
                        {" "}<b>{elsewhere.name}</b> now.</>
                    : <>Reading <b>{status.readIndex}</b> — new records only.
                        {" "}{recordsPhrase(elsewhere)} in <b>{elsewhere.name}</b>.</>)
                : <>Reading <b>{status.readIndex}</b> — every record is here.</>}
        {degraded && <> Filters match exact values only on this index.</>}
        {" "}
        <Link component="button" underline="hover" onClick={() => navigate("/services")}>
            Manage indices
        </Link>
    </Alert>;
}
